import { Building2 } from "lucide-react";
import { PageHeading, Section, Field, Steps, Callout, RoleBadge, Diagram, MockRow, MockField, MockButton } from "../shared";

export default function AdminDepartmentsSection() {
  return (
    <div data-testid="guide-admin-departments">
      <PageHeading
        icon={Building2}
        title="Departments & Sections"
        description="A simple two-level org tree: departments at the top, sections nested inside each one. It's being built now so the data is ready for future inter-departmental tracking and dossier filtering."
      />

      <Callout variant="info" title="In plain words">
        Think of this as a folder tree of your organisation. A <span className="text-white">department</span> is a big folder (like "Finance"); a <span className="text-white">section</span> is a sub-folder inside it (like "Treasury"). You're just building the list here — it doesn't change how items flow yet.
      </Callout>

      <Section title="Who can manage it">
        <div className="flex items-center gap-2"><RoleBadge role="admin" /><span className="text-xs text-white/55">Same permission as Team Members (<span className="text-white">manage_team_members</span>). Behaves like a directory edit.</span></div>
      </Section>

      <Callout variant="info" title="Status — preparatory">
        The Departments tree is captured here but is not yet wired into the Register form, KPI Dashboard, or routing logic. Building the tree now means the data is ready when those features arrive.
      </Callout>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          Use it to set up your organisation's structure so team members can be grouped by department and section. Add departments first, then add sections inside them.
        </p>
      </Section>

      <Section title="Adding a department and a section">
        <Diagram
          frameLabel="Admin → Departments & Sections"
          legend={[
            <>Click <span className="text-white">Add Department</span> and type its name.</>,
            <>Set a <span className="text-white">sort order</span> (lower numbers show first).</>,
            <>Expand the department, click <span className="text-white">Add Section</span>, and name the section.</>,
            <>Click <span className="text-white">Save</span> to store the row.</>,
          ]}
        >
          <MockButton label="Add Department" marker={1} />
          <MockRow>
            <MockField label="Name" value="Finance" />
            <MockField label="Sort order" value="10" marker={2} />
          </MockRow>
          <MockButton label="Add Section" marker={3} variant="ghost" />
          <MockField label="Section name" value="Treasury" />
          <MockButton label="Save" marker={4} />
        </Diagram>
        <Steps steps={[
          <>Click <span className="text-white">Add Department</span>, enter name + sort order, save.</>,
          <>Expand the department row → <span className="text-white">Add Section</span>, enter name + sort order, save.</>,
          <>Edit or archive at any time. Deletion removes the row outright; archive is the safer choice when historic data may reference it.</>,
        ]} />
      </Section>

      <Section title="Department fields">
        <div className="space-y-2">
          <Field name="Name" required="required"><p>Display name (e.g. "Finance", "Operations").</p></Field>
          <Field name="Sort order" required="optional"><p>Lower numbers appear first.</p></Field>
          <Field name="Archived" required="optional"><p>Hide from default views without breaking historical references. Toggle <span className="text-white">Show archived</span> at the top to bring them back into view.</p></Field>
        </div>
      </Section>

      <Section title="Section fields">
        <div className="space-y-2">
          <Field name="Department" required="required"><p>Parent department. Each section belongs to exactly one.</p></Field>
          <Field name="Name" required="required"><p>Section display name (e.g. "Treasury", "FX Desk").</p></Field>
          <Field name="Sort order" required="optional"><p>Within the parent department.</p></Field>
        </div>
      </Section>

      <Callout variant="warning" title="Archive instead of delete">
        Deletion removes the row outright. If any historic data may reference a department or section, use <span className="text-white">Archived</span> instead — it hides the row without breaking history.
      </Callout>
    </div>
  );
}
